import { useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Shimmer } from '@wartek-id/shimmer'

import { PAMONG_PRAKTIK_KINERJA_INDIKATOR_IMAGE } from 'app/KinerjaPamongPage/constants/praktik-kinerja'
import { PageGuardPamongPelaksanaanStatus } from 'app/KinerjaPamongPage/components/pelaksanaan/PageGuardPamongPelaksanaanStatus'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { Header } from 'app/KinerjaShared/components/Header'
import {
  SectionCard,
  SectionCardBody,
  SectionCardHeader,
} from 'app/KinerjaShared/components/SectionCard'
import { useAPIPelaksanaanDocumentPreparation } from 'api/kinerja/useAPIKinerja'
import { formatDate } from 'utils/datetime'
import { IndicatorCard } from 'app/KinerjaPage/pages/PelaksanaanV2/components/IndicatorCard'
import { useAPIPelaksanaanProgress } from 'api/kinerja/useAPIKinerja'
import { stripHTMLTags } from 'utils/text'
import { assetUrl } from 'utils/url'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import { PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'

import type { MultiPeriodPayload } from 'types/kinerja'

type RincianObservasiProps = {
  periodParams?: MultiPeriodPayload
}

export function RincianObservasi({ periodParams }: RincianObservasiProps) {
  const { periodNo, periodYear } = usePeriodContext()

  // -----------------------------------------
  // Resolve period params
  // -----------------------------------------
  const resolvedPeriodParams = useMemo(() => {
    if (periodParams) return periodParams

    // Default with current period
    return {
      skpPeriod: periodNo,
      skpYear: periodYear,
    }
  }, [periodParams, periodNo, periodYear])

  // -----------------------------------------
  // Get diskusi persiapan data
  // -----------------------------------------
  const {
    data: preparationDocumentData,
    isLoading: isPreparationDocumentLoading,
  } = useAPIPelaksanaanDocumentPreparation({
    tipe: 'SPNF',
    periodParams: {
      skp_period: resolvedPeriodParams.skpPeriod,
      skp_year: resolvedPeriodParams.skpYear,
    },
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const observationDate = useMemo(() => {
    return preparationDocumentData?.data?.detail?.observationDate
      ? formatDate(preparationDocumentData?.data?.detail?.observationDate)
      : '-'
  }, [preparationDocumentData?.data?.detail?.observationDate])

  // -----------------------------------------
  // Get pelaksanaan progress data
  // -----------------------------------------
  const {
    data: pelaksanaanProgressData,
    isLoading: isPelaksanaanProgressLoading,
  } = useAPIPelaksanaanProgress({
    periodParams: {
      skp_period: resolvedPeriodParams.skpPeriod,
      skp_year: resolvedPeriodParams.skpYear,
    },
    tipe: 'SPNF',
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  // -----------------------------------------
  // Resolve Praktik Kinerja asset path
  // -----------------------------------------
  const illustrationAssetPath = useMemo(() => {
    const icon =
      PAMONG_PRAKTIK_KINERJA_INDIKATOR_IMAGE[
        pelaksanaanProgressData?.data?.subIndicatorName
      ]

    if (icon) return assetUrl(`/illustrations/${icon}`)

    return ''
  }, [pelaksanaanProgressData?.data?.subIndicatorName])

  // -----------------------------------------
  // Handle back navigation with fallback
  // -----------------------------------------
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`,
  })

  return (
    <main className="pt-12 px-8 pb-8">
      <div className="mx-auto max-w-7xl">
        <Header
          title="Rincian Observasi Praktik Kinerja"
          className="mb-9"
          onBack={handleBack}
        />

        <PageGuardPamongPelaksanaanStatus
          periodParams={resolvedPeriodParams}
          targetStep="OBSERVATION_IMPLEMENTATION"
        >
          <SectionCard>
            <SectionCardHeader variant="light-blue">
              <Text as="h2" variant="heading-lg">
                Langkah-langkah Observasi Praktik Kinerja
              </Text>
            </SectionCardHeader>

            <SectionCardBody className="px-10 py-6">
              <div className="max-w-5xl">
                <Step number={1}>
                  <Text as="h3" variant="heading-sm" className="mb-1">
                    Observasi akan dilakukan sesuai jadwal
                  </Text>
                  <div className="mb-1">
                    <Text variant="body" color="subdued" className="mb-3">
                      Berikut jadwal observasi Anda:
                    </Text>
                    <dl className="space-y-2">
                      {isPreparationDocumentLoading ? (
                        <Shimmer noOfLines={5} className="!w-96 !h-5" />
                      ) : (
                        <>
                          <div className="flex">
                            <Text as="dt" className="w-60" color="subdued">
                              Tanggal
                            </Text>
                            <Text
                              as="dd"
                              color="subdued"
                              className="overflow-wrap-anywhere"
                            >
                              :&nbsp;{observationDate}
                            </Text>
                          </div>
                          <div className="flex">
                            <Text as="dt" className="w-60" color="subdued">
                              Pertemuan
                            </Text>
                            <Text
                              as="dd"
                              color="subdued"
                              className="overflow-wrap-anywhere"
                            >
                              :&nbsp;
                              {preparationDocumentData?.data?.detail
                                ?.observationMeeting || '-'}
                            </Text>
                          </div>
                          <div className="flex">
                            <Text as="dt" className="w-60" color="subdued">
                              Perangkat Pendukung
                            </Text>
                            <Text
                              as="dd"
                              color="subdued"
                              className="overflow-wrap-anywhere"
                            >
                              :&nbsp;
                              {preparationDocumentData?.data?.detail
                                ?.observationTool || '-'}
                            </Text>
                          </div>
                          <div className="flex">
                            <Text as="dt" className="w-60" color="subdued">
                              Catatan Lain (Opsional)
                            </Text>
                            <Text
                              as="dd"
                              color="subdued"
                              className="overflow-wrap-anywhere"
                            >
                              :&nbsp;
                              {preparationDocumentData?.data?.detail
                                ?.observationNote || '-'}
                            </Text>
                          </div>
                        </>
                      )}
                    </dl>
                  </div>
                </Step>

                <Step number={2}>
                  <Text as="h3" variant="heading-sm" className="mb-1">
                    Kepala SKB akan mengisi Hasil Observasi
                  </Text>
                  <Text variant="body" color="subdued" className="mb-1">
                    Hasil observasi dapat didiskusikan untuk Pelaksanaan Tindak
                    Lanjut nantinya.
                  </Text>

                  <IndicatorCard
                    isLoading={isPelaksanaanProgressLoading}
                    className="mb-10"
                    illustrationAssetPath={illustrationAssetPath}
                    subIndicatorName={
                      pelaksanaanProgressData?.data?.subIndicatorName
                    }
                    subIndicatorDefinition={
                      pelaksanaanProgressData?.data?.subIndicatorDefinition
                    }
                    contentId={pelaksanaanProgressData?.data?.contentId}
                    qualityTarget={pelaksanaanProgressData?.data?.qualityTarget}
                  />

                  <div className="flex gap-10">
                    {isPreparationDocumentLoading ? (
                      <>
                        <Shimmer className="!h-28 !w-full" />
                        <Shimmer className="!h-28 !w-full" />
                      </>
                    ) : (
                      <>
                        <div className="w-1/2 space-y-1">
                          <Text variant="heading-sm">Target Perilaku</Text>
                          <ul className="list-disc pl-5 space-y-1">
                            {preparationDocumentData?.data?.detail?.behavioralTargets?.map(
                              (item, index) => (
                                <Text key={`behavior_${index}`} as="li">
                                  {item.behavioralTarget}
                                </Text>
                              )
                            )}
                          </ul>
                        </div>
                        <div className="w-1/2 space-y-1">
                          <Text variant="heading-sm">Upaya Mempelajari</Text>
                          <Text className="simple-wysiwyg-content overflow-wrap-anywhere">
                            {stripHTMLTags(
                              preparationDocumentData?.data?.detail
                                ?.behavioralTargetEffort
                            )}
                          </Text>
                        </div>
                      </>
                    )}
                  </div>
                </Step>

                <Step number={3} hideStepLine={false}>
                  <Text as="h3" variant="heading-sm" className="mb-1">
                    Persiapkan dengan matang dan semangat!
                  </Text>
                  <Text variant="body" color="subdued" className="mb-1">
                    Pelajari Rubrik Observasi dan informasi lainnya. Semoga
                    lancar, ya.
                  </Text>
                </Step>

                <p className="p-4">
                  <Text as="span" variant="body-bold" className="mb-1">
                    Perlu diperhatikan bahwa:
                  </Text>
                  <br />
                  <Text as="span" variant="body">
                    Rubrik Observasi hanya ditujukan untuk membantu Kepala SKB
                    ketika observasi berlangsung.
                  </Text>
                </p>
              </div>
            </SectionCardBody>
          </SectionCard>
        </PageGuardPamongPelaksanaanStatus>
      </div>
    </main>
  )
}

function Step({
  hideStepLine = true,
  number,
  children,
}: {
  hideStepLine?: boolean
  number: number
  children: React.ReactNode
}) {
  return (
    <div className="gap-6 flex w-full">
      <div className="flex flex-col items-center gap-2">
        <div className="relative flex items-center justify-center rounded-full bg-blue-70 p-4">
          <Text as="span" color="inverse" className="absolute">
            {number}
          </Text>
        </div>

        {hideStepLine && <div className="w-px h-full bg-gray-20 mb-2" />}
      </div>

      <div className="pb-12 mb-2 w-full">{children}</div>
    </div>
  )
}
